/**
 * DecisionBox - Encart de décision rapide (10 secondes)
 * Affiche uniquement des données vérifiées: CMR, VLEP, SVHC
 * Aucune phrase médicale non sourcée
 */

import { CMRStatus, SourceINRS, SourceECHA } from '../../services/api'
import Badge from '../ui/Badge'

interface DecisionBoxProps {
  cmr: CMRStatus | null
  inrs: SourceINRS
  echa: SourceECHA
}

export default function DecisionBox({ cmr, inrs, echa }: DecisionBoxProps) {
  // Déterminer le niveau d'alerte basé sur données vérifiées
  const isCMR = cmr?.est_cmr === true
  const isSVHC = echa?.svhc === true
  const hasVLEP = inrs?.vlep_8h !== null

  // Niveau de criticité
  const criticalLevel = isCMR || isSVHC ? 'critical' : hasVLEP ? 'warning' : 'info'

  // Construire les points clés (uniquement données sourcées)
  const keyPoints: string[] = []

  if (isCMR && cmr) {
    const catText = cmr.categorie ? ` (${cmr.categorie})` : ''
    keyPoints.push(`Classement CMR${catText}`)
  }

  if (isSVHC) {
    keyPoints.push('Substance SVHC (ECHA)')
  }

  if (hasVLEP && inrs.vlep_8h) {
    keyPoints.push(`VLEP-8h: ${inrs.vlep_8h.valeur} ${inrs.vlep_8h.unite}`)
  }

  if (inrs?.vlep_court_terme) {
    keyPoints.push(`VLEP court terme: ${inrs.vlep_court_terme.valeur} ${inrs.vlep_court_terme.unite}`)
  }

  // Si aucune donnée critique disponible
  if (keyPoints.length === 0) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-2">
          <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="font-medium text-gray-600">Informations limitées</span>
        </div>
        <p className="text-sm text-gray-500">
          Aucune donnée CMR, SVHC ou VLEP disponible dans les sources intégrées (V1).
        </p>
      </div>
    )
  }

  // Styles selon niveau de criticité
  const styles = {
    critical: {
      container: 'bg-red-50 border-red-300',
      icon: 'text-red-600',
      title: 'text-red-800',
      badge: 'bg-red-600 text-white',
      text: 'text-red-700'
    },
    warning: {
      container: 'bg-amber-50 border-amber-300',
      icon: 'text-amber-600',
      title: 'text-amber-800',
      badge: 'bg-amber-600 text-white',
      text: 'text-amber-700'
    },
    info: {
      container: 'bg-blue-50 border-blue-300',
      icon: 'text-blue-600',
      title: 'text-blue-800',
      badge: 'bg-blue-600 text-white',
      text: 'text-blue-700'
    }
  }

  const style = styles[criticalLevel]

  return (
    <div className={`border-2 rounded-lg p-4 ${style.container}`}>
      {/* Header avec badge CMR si applicable */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {criticalLevel === 'critical' ? (
            <svg className={`w-6 h-6 ${style.icon}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          ) : (
            <svg className={`w-6 h-6 ${style.icon}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          )}
          <span className={`font-semibold ${style.title}`}>
            {criticalLevel === 'critical' ? 'Attention requise' : 'Points de vigilance'}
          </span>
        </div>

        {/* Badge CMR dans l'encart (D6 Option C) - variantes uniformisées */}
        {isCMR && cmr && (
          <Badge
            variant={cmr.categorie?.includes('1') ? 'cmr1' : 'cmr2'}
            size="sm"
          >
            CMR {cmr.categorie || ''}
          </Badge>
        )}
      </div>

      {/* Points clés */}
      <ul className="space-y-1">
        {keyPoints.map((point, index) => (
          <li key={index} className={`flex items-center gap-2 text-sm ${style.text}`}>
            <span className="w-1.5 h-1.5 rounded-full bg-current flex-shrink-0" />
            {point}
          </li>
        ))}
      </ul>

      {/* Mention SVHC détail si applicable */}
      {isSVHC && echa.raison_inclusion && (
        <p className={`mt-2 text-xs ${style.text} opacity-80`}>
          Raison SVHC: {echa.raison_inclusion}
        </p>
      )}

      {/* CMR détail si applicable */}
      {isCMR && cmr?.detail && (
        <p className={`mt-2 text-xs ${style.text} opacity-80`}>
          {cmr.detail}
        </p>
      )}
    </div>
  )
}
